import * as Yup from "yup";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import HelpIcon from "@material-ui/icons/Help";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";

import BaseInput from "../../../../../components/elements/BaseInput";
import Button from "../../../../../components/elements/Button";
import FormFieldError from "../../../../../components/elements/FormFieldError";
import classes from "./Controls.module.scss";

type FormInitialValues = {
  customUrl: string;
};

type CustomUrlModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  handleSave: (url: string) => void;
};

const tooltips = {
  urlFormat: (
    <>
      The entered URL must match the following format:
      <br />- It may start with <strong>http://</strong> or <strong>https://</strong> (optional).
      <br />
      - The domain must contain only letters, numbers, and hyphens, but hyphens cannot be at the beginning or end of the
      domain.
      <br />
      - The domain extension must be at least 2 letters long.
      <br />- Spaces and special characters are not allowed, except for parameters.
    </>
  ),
};

const CustomUrlModal: FC<CustomUrlModalProps> = ({ isOpen, handleClose, handleSave }) => {
  const formInitialValues: FormInitialValues = {
    customUrl: "",
  };

  const validationSchema = Yup.object({
    customUrl: Yup.string()
      .required("Required field.")
      .transform((value) => value.trim())
      .matches(
        /^(?:ht{2}ps?:\/{2})?(?:[\dA-Za-z-]+\.)+[A-Za-z]{2,}(?::\d+)?(?:\/[\w/-]*)?(\?[^\s#]*)?(#\S*)?$/,
        "Invalid URL format",
      ),
  });

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    const { customUrl } = values;
    handleSave(customUrl);
    actions.resetForm();
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="Enter Custom Url Modal"
      className={classes.modalContainer}
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ values }) => (
          <>
            <div className={`${classes.dialogTitle} ${classes.dialogTitleWrap}`}>
              <Typography variant="h2">Enter Custom URL</Typography>
              <Tooltip
                title={
                  <Typography variant="body2" style={{ lineHeight: 1.5 }}>
                    {tooltips.urlFormat}
                  </Typography>
                }
              >
                <HelpIcon />
              </Tooltip>
            </div>
            <Form>
              <DialogContent className={classes.dialogContent}>
                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.customUrl}
                    name="customUrl"
                    variant="outlined"
                    label="Enter a custom URL... *"
                  />

                  <FormFieldError name="customUrl" />
                </div>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button type="submit">Save Custom URL</Button>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default CustomUrlModal;
